import attributeMap from "./map/attributeMap.json";
import elementMap from "./map/elementMap.json";
import valuelistMap from "./map/valuelistMap.json";
import collectionMap from "./map/collectionMap.json";
import { DOMParser, XMLSerializer } from "@xmldom/xmldom";
import JSZip from "jszip";
import xpath from "xpath";

const renameElementPreservePrefix = (doc: Document, node: Node, newLocalName: string) => {
  const el = node as Element;

  const ns = el.namespaceURI;     // same Namespace-URI like before
  const prefix = el.prefix;       // exact same Prefix-String like before (ex. "vpi08" or "ns4")
  const qname = prefix ? `${prefix}:${newLocalName}` : newLocalName;

  const newNode = ns
    ? doc.createElementNS(ns, qname)
    : doc.createElement(qname);

  // copy attribute 1:1 (incl. Prefix/Namespace), xmlns-* NO double
  for (let i = 0; i < el.attributes.length; i++) {
    const a = el.attributes[i];
    const isXmlns = a.name === "xmlns" || a.prefix === "xmlns";
    if (isXmlns) continue;

    if (a.namespaceURI) {
      // a.name contains original prefix (ex. "ns3:differentiator")
      newNode.setAttributeNS(a.namespaceURI, a.name, a.value);
    } else {
      newNode.setAttribute(a.name, a.value);
    }
  }

  // reassign children
  while (el.firstChild) newNode.appendChild(el.firstChild);

  el.parentNode?.replaceChild(newNode, el);
};

// Inserts a new parent with the same namespace/prefix as `child`, moves `child` under it
const wrapElementPreservePrefix = (doc: Document, child: Element, wrapperLocalName: string) => {
  const ns = child.namespaceURI;
  const prefix = child.prefix;
  const qname = prefix ? `${prefix}:${wrapperLocalName}` : wrapperLocalName;
  const wrapper = ns
    ? doc.createElementNS(ns, qname)
    : doc.createElement(qname);

  const parent = child.parentNode;
  if (!parent) return;

  parent.insertBefore(wrapper, child);
  wrapper.appendChild(child);
};

const convertXmlContent = (xmlString: string): string => {
  const doc = new DOMParser().parseFromString(xmlString, "text/xml");

  const NS = {
    m: "http://www.vpihamburg.de/vpi08/messages/",
    o: "http://www.vpihamburg.de/vpi08/orders/",
    t: "http://www.vpihamburg.de/vpi08/types/",
    g: "http://www.vpihamburg.de/vpi08/general/",
  };

  // xpath need the namespaces
  const select = xpath.useNamespaces({
    // “canon” prefixes:
    m: NS.m,
    o: NS.o,
    t: NS.t,
    g: NS.g,

    // optional: Legacy-Prefixes:
    vpi08: NS.m,
    vpi08orders: NS.o,
    vpi08types: NS.t,
    vpi08general: NS.g,

    // optional: customer-Prefixe (ns2/ns3/ns4)
    ns2: NS.o,
    ns3: NS.t,
    ns4: NS.g,
  });

  // rename wagen to wagon
  const wagen = select("//m:Wagen", doc);
  if (Array.isArray(wagen) && wagen.length) {
    renameElementPreservePrefix(doc, wagen[0], "Wagon");
  }

  // rename radsatz to wheelset
  const radsatz = select("//m:Radsatz", doc);
  if (Array.isArray(radsatz) && radsatz.length) {
    renameElementPreservePrefix(doc, radsatz[0], "Wheelset");
  }

  // rename attributes
  attributeMap.forEach(({ oldTag, newTag, parent }) => {
    const nodes: Node[] = [];
    if (parent === "Wagon") {
      const technicalWaggonIncoming = select(
        `//g:technicalWaggonIncoming/t:${oldTag}`,
        doc
      );
      if (Array.isArray(technicalWaggonIncoming)) {
        nodes.push(...technicalWaggonIncoming);
      }
      const technicalWaggonOutgoing = select(
        `//g:technicalWaggonOutgoing/t:${oldTag}`,
        doc
      );
      if (Array.isArray(technicalWaggonOutgoing)) {
        nodes.push(...technicalWaggonOutgoing);
      }
    } else if (parent === "Wheelset") {
      const technicalWheelsetIncoming = select(
        `//g:technicalWheelsetIncoming/t:${oldTag}`,
        doc
      );
      if (Array.isArray(technicalWheelsetIncoming)) {
        nodes.push(...technicalWheelsetIncoming);
      }
      const technicalWheelsetOutgoing = select(
        `//g:technicalWheelsetOutgoing/t:${oldTag}`,
        doc
      );
      if (Array.isArray(technicalWheelsetOutgoing)) {
        nodes.push(...technicalWheelsetOutgoing);
      }
    }
    const found = select(`//t:${parent}/t:${oldTag}`, doc);
    if (Array.isArray(found)) {
      nodes.push(...found);
    }

    nodes.forEach((node: Node) => {
      renameElementPreservePrefix(doc, node, newTag);
    });
  });

  // rename elements
  elementMap.forEach(({ oldTag, newTag }) => {
    const nodes = select(`//t:${oldTag}`, doc);
    if (Array.isArray(nodes)) {
      nodes.forEach((node: Node) => {
        renameElementPreservePrefix(doc, node, newTag);
      });
    }
  });

  // rename collections (optional wrapWith: new parent before rename)
  collectionMap.forEach((entry) => {
    const { oldTag, newTag, wrapWith } = entry as {
      oldTag: string;
      newTag: string;
      wrapWith?: string;
    };
    const nodes = select(`//t:${oldTag}`, doc);
    if (Array.isArray(nodes)) {
      nodes.forEach((node: Node) => {
        const el = node as Element;
        if (wrapWith) {
          wrapElementPreservePrefix(doc, el, wrapWith);
          renameElementPreservePrefix(doc, el, newTag);
        } else {
          renameElementPreservePrefix(doc, node, newTag);
        }
      });
    }
  });

  const findAttrByNsLocal = (el: Element, ns: string, local: string): Attr | null => {
    for (let i = 0; i < el.attributes.length; i++) {
      const a = el.attributes[i];
      if (a.namespaceURI === ns && a.localName === local) return a;
    }
    return null;
  };
  // rename valuelists "t:type"
  const allElements = select("//*", doc);
  if (Array.isArray(allElements)) {
    allElements.forEach((node: Node) => {
      const el = node as Element;
      // rename valuelists "*:type" (either ns3:type or vpi08types:type)
      const typeAttr = findAttrByNsLocal(el, NS.t, "type");
      if (typeAttr) {
        const oldVal = typeAttr.value;
        const newVal = valuelistMap.find((v) => v.oldTag === oldVal)?.newTag;
        if (newVal) {
          // IMPORTANT: typeAttr.name contains original prefix
          el.setAttributeNS(NS.t, typeAttr.name, newVal);
        }
      }

      // rename differentiator "*:differentiator"
      const diffAttr = findAttrByNsLocal(el, NS.t, "differentiator");
      if (diffAttr) {
        const diffVal = diffAttr.value;
        if (diffVal) {
          const renameRule = attributeMap.find((rule) => rule.oldTag === diffVal);
          if (renameRule && el.localName === renameRule.parent) {
            el.setAttributeNS(NS.t, diffAttr.name, renameRule.newTag);
          }
        }
      }
    });
  }

  // update XML version
  const version = select("//m:VPI_version", doc);
  if (Array.isArray(version)) {
    version.forEach((node: Node) => {
      node.textContent = "5.0";
    });
  }

  return new XMLSerializer().serializeToString(doc);
};

type ProgressCallback = (additionalProgress: number) => void;

const parseZipFile = async (
  file: Blob,
  onProgress?: ProgressCallback
): Promise<Blob> => {
  const zipFile = new JSZip();
  await zipFile.loadAsync(file);
  const newZip = new JSZip();

  const numberOfFiles = Object.keys(zipFile.files).length;
  const progressPerFile = 100 / numberOfFiles;

  for (const [fileName, zipEntry] of Object.entries(zipFile.files)) {
    if (zipEntry.dir) continue;
    if (
      !fileName.startsWith("__MACOSX/") &&
      !fileName.startsWith("._") &&
      fileName.toLowerCase().endsWith(".xml")
    ) {
      // none xml files (and mac meta files) will stay unedited
      const fileAsString = await zipEntry.async("string");
      const parsedFileAsString = convertXmlContent(fileAsString);
      const fileExtension = fileName.substring(fileName.lastIndexOf("."));
      const convertedFileName = fileName.replace(
      fileExtension,
      `_v5_0${fileExtension}`,
    );
      newZip.file(convertedFileName, parsedFileAsString);
    } else {
      const content = await zipEntry.async("blob");
      newZip.file(fileName, content);
    }

    if (onProgress) onProgress(progressPerFile);
  }
  return await newZip.generateAsync({ type: "blob", streamFiles: true });
};

const parseXmlFile = async (
  file: Blob,
  onProgress?: ProgressCallback
): Promise<Blob> => {
  const fileAsString = await file.text();
  const parsedFileAsString = convertXmlContent(fileAsString);

  if (onProgress) onProgress(100);
  return new Blob([parsedFileAsString], {
    type: file.type || "application/xml",
  });
};

const parseFile = async (
  file: Blob,
  isZip: boolean,
  onProgress: ProgressCallback
): Promise<Blob> => {
  return isZip
    ? parseZipFile(file, onProgress)
    : parseXmlFile(file, onProgress);
};

export { parseFile, parseZipFile, parseXmlFile };
